import React, { useState, useEffect, useRef } from 'react';
import { Check, X, ChevronRight, ShieldCheck, Sparkles, Info, MessageCircle, Target, ClipboardCheck, Shield, Radio, Timer, TrendingUp } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { toVous, toVousFlat } from '../lib/toTu';
import { SlideBienvenue, SlideMerci } from './RvpSlidesPersonnalisees';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Alias courts pour appliquer toVous sur strings & items
const V = (s) => toVous(s || '');
const VF = (v) => toVousFlat(v);

/* ────────────── Slide : Titre ────────────── */
export function SlideTitle({ slide }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-8 sm:px-16">
      {slide.kicker && (
        <div className="rvp-fade-down text-[11.5px] sm:text-[13px] uppercase tracking-[0.3em] text-red-400 font-mono-spec font-bold mb-6">{slide.kicker}</div>
      )}
      <h1 className="rvp-zoom-in font-serif-editorial text-white text-[42px] sm:text-[72px] lg:text-[92px] leading-[0.95] tracking-tight" style={{ animationDelay: '0.15s' }}>{V(slide.title)}</h1>
      {slide.subtitle && (
        <p className="rvp-slide-up mt-6 sm:mt-8 text-white/70 text-[17px] sm:text-[22px] max-w-3xl leading-snug" style={{ animationDelay: '0.45s' }}>{V(slide.subtitle)}</p>
      )}
      {slide.footer && (
        <div className="rvp-fade-down absolute bottom-8 left-0 right-0 text-center text-white/40 text-[11.5px] font-mono-spec uppercase tracking-[0.22em]" style={{ animationDelay: '0.7s' }}>{slide.footer}</div>
      )}
    </div>
  );
}

/* ────────────── Slide : Section ────────────── */
export function SlideSection({ slide }) {
  return (
    <div className="h-full flex items-center justify-center px-8 sm:px-16">
      <div className="max-w-4xl">
        <div className="rvp-pop-in font-mono-spec text-red-500 text-[80px] sm:text-[140px] font-bold leading-none">{slide.number}</div>
        <h2 className="rvp-slide-up mt-4 font-serif-editorial text-white text-[36px] sm:text-[60px] leading-tight" style={{ animationDelay: '0.25s' }}>{V(slide.title)}</h2>
        {slide.note && <p className="rvp-slide-up mt-4 text-white/60 text-[15px] sm:text-[18px]" style={{ animationDelay: '0.45s' }}>{V(slide.note)}</p>}
      </div>
    </div>
  );
}

/* ────────────── Slide : Contenu (bullets) ────────────── */
export function SlideContent({ slide }) {
  return (
    <div className="h-full flex flex-col justify-center px-8 sm:px-16 py-10">
      <div className="max-w-5xl mx-auto w-full">
        {slide.kicker && (
          <div className="rvp-fade-down text-[10.5px] sm:text-[12px] uppercase tracking-[0.28em] text-red-400 font-mono-spec font-bold mb-3">{slide.kicker}</div>
        )}
        <h2 className="rvp-slide-up font-serif-editorial text-white text-[28px] sm:text-[42px] leading-tight mb-8 sm:mb-10" style={{ animationDelay: '0.1s' }}>{slide.title}</h2>
        <ul className="space-y-4 sm:space-y-5">
          {slide.bullets.map((b, i) => (
            <li key={i} className="rvp-slide-in flex items-start gap-3 sm:gap-4 text-white/85 text-[15px] sm:text-[19px] leading-relaxed" style={{ animationDelay: `${0.3 + i * 0.14}s` }}>
              <ChevronRight size={20} className="shrink-0 text-red-500 mt-1" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
        {slide.footnote && (
          <div className="rvp-slide-up mt-8 text-[12px] sm:text-[13px] text-white/40 font-mono-spec border-l-2 border-white/15 pl-3 italic" style={{ animationDelay: `${0.4 + slide.bullets.length * 0.14}s` }}>{slide.footnote}</div>
        )}
      </div>
    </div>
  );
}

/* ────────────── Slide : Stat (chiffre choc) ────────────── */
export function SlideStat({ slide }) {
  return (
    <div className="h-full flex items-center justify-center px-8 sm:px-16">
      <div className="max-w-4xl text-center">
        <div className="rvp-impact font-serif-editorial text-red-500 text-[100px] sm:text-[180px] lg:text-[220px] leading-[0.9] tracking-tight">{slide.number}</div>
        <div className="rvp-slide-up mt-4 text-white text-[20px] sm:text-[30px] font-serif-editorial leading-tight" style={{ animationDelay: '0.5s' }}>{V(slide.label)}</div>
        {slide.context && <p className="rvp-slide-up mt-6 text-white/60 text-[14px] sm:text-[17px] max-w-2xl mx-auto leading-relaxed" style={{ animationDelay: '0.75s' }}>{V(slide.context)}</p>}
        <div className="rvp-fade-down mt-8 text-[11.5px] sm:text-[12.5px] uppercase tracking-[0.22em] text-white/40 font-mono-spec" style={{ animationDelay: '1s' }}>Source : {slide.source}</div>
      </div>
    </div>
  );
}

/* ────────────── Slide : QCM (interactif + poll live optionnel) ────────────── */
export function SlideQcm({ slide, pollContext, forceReveal }) {
  const [picked, setPicked] = useState(null);
  const revealed = picked !== null || forceReveal;
  const [results, setResults] = useState(null);
  const pollActive = !!pollContext?.sessionId && slide.pollable !== false;
  const [pulseTotal, setPulseTotal] = useState(false);
  const prevTotalRef = useRef(0);
  const confettiFiredRef = useRef(false);

  // Poll live: fetch results every 2s
  useEffect(() => {
    if (!pollActive) return;
    let alive = true;
    const fetchResults = async () => {
      try {
        const url = `${API}/poll/results/${pollContext.sessionId}/${pollContext.slug}/${pollContext.qIndex}`;
        const r = await axios.get(url);
        if (alive) {
          const newTotal = r.data?.total || 0;
          if (newTotal > prevTotalRef.current) {
            setPulseTotal(true);
            setTimeout(() => setPulseTotal(false), 600);
          }
          prevTotalRef.current = newTotal;
          setResults(r.data);
        }
      } catch (e) { /* silent */ }
    };
    fetchResults();
    const iv = setInterval(fetchResults, 2000);
    return () => { alive = false; clearInterval(iv); };
  }, [pollActive, pollContext?.sessionId, pollContext?.slug, pollContext?.qIndex]);

  // Confetti burst when reveal happens (once per slide)
  useEffect(() => {
    if (forceReveal && !confettiFiredRef.current) {
      confettiFiredRef.current = true;
      const duration = 1200;
      const end = Date.now() + duration;
      const colors = ['#ef4444', '#10b981', '#f59e0b', '#ffffff'];
      (function frame() {
        confetti({
          particleCount: 4,
          angle: 60,
          spread: 65,
          origin: { x: 0, y: 0.7 },
          colors,
          scalar: 0.9,
        });
        confetti({
          particleCount: 4,
          angle: 120,
          spread: 65,
          origin: { x: 1, y: 0.7 },
          colors,
          scalar: 0.9,
        });
        if (Date.now() < end) requestAnimationFrame(frame);
      })();
    }
    if (!forceReveal) confettiFiredRef.current = false;
  }, [forceReveal]);

  const total = results?.total || 0;
  const voteUrl = pollActive
    ? `${window.location.origin}/vote/${pollContext.sessionId}/${pollContext.slug}/${pollContext.qIndex}`
    : null;
  const eleveCount = results?.by_role?.eleve ? Object.values(results.by_role.eleve).reduce((a,b)=>a+b,0) : 0;
  const parentCount = results?.by_role?.parent ? Object.values(results.by_role.parent).reduce((a,b)=>a+b,0) : 0;

  return (
    <div className="h-full flex flex-col justify-center px-6 sm:px-12 py-6">
      <div className="max-w-6xl mx-auto w-full">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="text-[10.5px] sm:text-[12px] uppercase tracking-[0.28em] text-red-400 font-mono-spec font-bold inline-flex items-center gap-2">
            {pollActive ? (
              <>
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                </span>
                <Radio size={13} /> QCM en direct · scanner pour voter
              </>
            ) : (
              <><Sparkles size={13} /> QCM interactif</>
            )}
          </div>
          {pollActive && total > 0 && (
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border transition-all duration-500 ${pulseTotal ? 'scale-110 border-emerald-400 bg-emerald-500/20 shadow-[0_0_25px_-4px_rgba(16,185,129,0.7)]' : 'border-emerald-500/40 bg-emerald-950/25'}`}>
              <TrendingUp size={12} className="text-emerald-300" />
              <span className="text-[13px] font-mono-spec font-bold text-emerald-200">{total} vote{total > 1 ? 's' : ''}</span>
              {(eleveCount > 0 || parentCount > 0) && (
                <span className="text-[11px] font-mono-spec text-white/50">· {eleveCount} élève · {parentCount} parent</span>
              )}
            </div>
          )}
        </div>
        <h2 className="rvp-slide-up font-serif-editorial text-white text-[22px] sm:text-[32px] leading-tight mb-5 sm:mb-6">{V(slide.question)}</h2>

        <div className={pollActive ? 'grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6' : ''}>
          <div className="grid sm:grid-cols-2 gap-3">
            {slide.options.map((opt, i) => {
              const isPicked = picked === i;
              const showCorrect = revealed && opt.correct;
              const showWrong = revealed && isPicked && !opt.correct;
              const votes = parseInt(results?.counts?.[String(i)] || 0, 10);
              const pct = total > 0 ? Math.round((votes / total) * 100) : 0;
              const isTop = total > 0 && pct > 0 && Object.entries(results?.counts || {}).every(([k, v]) => parseInt(v, 10) <= votes || parseInt(k, 10) === i);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => !revealed && setPicked(i)}
                  data-testid={`qcm-opt-${i}`}
                  style={{ animationDelay: `${0.2 + i * 0.12}s` }}
                  className={`rvp-pop-in relative overflow-hidden text-left h-auto min-h-[60px] px-4 py-3 rounded-xl border text-[14.5px] sm:text-[16px] font-semibold transition-all duration-300 ${
                    showCorrect ? 'border-emerald-500 bg-emerald-950/40 text-emerald-100 scale-[1.02] shadow-[0_0_30px_-6px_rgba(16,185,129,0.6)]' :
                    showWrong ? 'border-red-500 bg-red-950/40 text-red-100' :
                    revealed ? 'border-white/10 bg-white/[0.03] text-white/40 opacity-60' :
                    isTop && total > 0 ? 'border-sky-400/60 bg-sky-950/25 text-white shadow-[0_0_20px_-6px_rgba(56,189,248,0.4)]' :
                    'border-white/15 bg-white/[0.04] text-white hover:border-red-500/50 hover:bg-red-950/20'
                  }`}
                >
                  {pollActive && total > 0 && (
                    <div className={`absolute inset-y-0 left-0 ${showCorrect ? 'bg-emerald-500/25' : showWrong ? 'bg-red-500/20' : 'bg-sky-500/15'} transition-all duration-700 ease-out`} style={{ width: `${pct}%` }} />
                  )}
                  <div className="relative flex items-start gap-3">
                    <span className={`shrink-0 w-7 h-7 rounded-full border font-mono-spec text-[13px] flex items-center justify-center transition-all ${
                      showCorrect ? 'border-emerald-400 bg-emerald-500/30 text-emerald-100 scale-110' :
                      showWrong ? 'border-red-400 bg-red-500/20 text-red-200' :
                      'border-white/25 text-white/70'
                    }`}>
                      {showCorrect ? <Check size={13} /> : showWrong ? <X size={13} /> : String.fromCharCode(65 + i)}
                    </span>
                    <span className="flex-1">{V(opt.text)}</span>
                    {pollActive && total > 0 && (
                      <span className={`shrink-0 text-[13px] font-mono-spec font-bold ${showCorrect ? 'text-emerald-200' : 'text-white/70'}`}>{pct}%</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          {pollActive && voteUrl && (
            <div className="rounded-xl border border-emerald-500/25 bg-emerald-950/10 p-4 flex flex-col items-center gap-2 min-w-[200px]" data-testid="qcm-qr">
              <div className="text-[10.5px] uppercase tracking-wider font-mono-spec text-emerald-300 font-bold inline-flex items-center gap-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                Scanner pour voter
              </div>
              <div className="bg-white p-2 rounded-lg">
                <QRCodeSVG value={voteUrl} size={160} level="M" />
              </div>
              <div className="text-white/50 text-[10.5px] font-mono-spec text-center">Session · <span className="text-emerald-300 font-bold text-[12px] tracking-wider">{pollContext.sessionId}</span></div>
              <div className="text-white/40 text-[10px] font-mono-spec break-all text-center max-w-[180px]">{voteUrl.replace(/^https?:\/\//, '')}</div>
            </div>
          )}
        </div>

        {revealed && (
          <div className="mt-5 rounded-xl border border-sky-500/40 bg-sky-950/25 p-4 animate-[fadeIn_0.4s_ease-out]">
            <div className="flex items-start gap-3">
              <Info size={18} className="text-sky-300 mt-0.5 shrink-0" />
              <div>
                <div className="text-sky-300 text-[12px] uppercase tracking-wider font-bold font-mono-spec">Explication</div>
                <p className="mt-1.5 text-white/85 text-[13.5px] sm:text-[15px] leading-relaxed">{V(slide.explanation)}</p>
                {slide.engagement && (
                  <div className="mt-3 pt-3 border-t border-sky-500/20 text-[12.5px] sm:text-[14px] text-white/75">
                    <span className="text-amber-300 font-bold">Engagement famille · </span>{V(slide.engagement)}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ────────────── Slide : Video (YouTube embed) ────────────── */
export function SlideVideo({ slide }) {
  return (
    <div className="h-full flex flex-col justify-center px-8 sm:px-16 py-10">
      <div className="max-w-5xl mx-auto w-full">
        <h2 className="font-serif-editorial text-white text-[26px] sm:text-[40px] leading-tight mb-6">{V(slide.title)}</h2>
        <div className="rounded-xl overflow-hidden border border-white/15 aspect-video bg-black">
          <iframe
            src={`https://www.youtube.com/embed/${slide.youtube_id}?rel=0&modestbranding=1`}
            title={slide.title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        {slide.note && <p className="mt-4 text-white/60 text-[13px] sm:text-[15px]">{V(slide.note)}</p>}
      </div>
    </div>
  );
}

/* ────────────── Slide : Image ────────────── */
export function SlideImage({ slide }) {
  return (
    <div className="h-full flex flex-col justify-center px-8 sm:px-16 py-10">
      <div className="max-w-5xl mx-auto w-full">
        <img src={slide.image_url} alt={slide.caption} className="w-full rounded-xl border border-white/15 object-cover max-h-[70vh]" />
        {slide.caption && <p className="mt-4 text-white/60 text-[13px] sm:text-[15px] italic text-center">{V(slide.caption)}</p>}
      </div>
    </div>
  );
}

/* ────────────── Slide : Citation ────────────── */
export function SlideQuote({ slide }) {
  return (
    <div className="h-full flex items-center justify-center px-8 sm:px-16">
      <div className="max-w-4xl text-center">
        <div className="text-red-500 font-serif-editorial text-[80px] sm:text-[140px] leading-none opacity-40">&ldquo;</div>
        <blockquote className="font-serif-editorial text-white text-[26px] sm:text-[38px] lg:text-[46px] leading-snug italic -mt-6">
          {V(slide.text)}
        </blockquote>
        {slide.author && <div className="mt-6 text-white/60 text-[13px] sm:text-[15px] font-mono-spec uppercase tracking-wider">— {slide.author}</div>}
      </div>
    </div>
  );
}

/* ────────────── Slide : Engagement ────────────── */
export function SlideEngagement({ slide }) {
  return (
    <div className="h-full flex flex-col justify-center px-8 sm:px-16 py-10">
      <div className="max-w-4xl mx-auto w-full">
        <div className="text-[10.5px] sm:text-[12px] uppercase tracking-[0.28em] text-emerald-400 font-mono-spec font-bold mb-3 inline-flex items-center gap-2">
          <ShieldCheck size={13} /> Engagement famille
        </div>
        <h2 className="rvp-slide-up font-serif-editorial text-white text-[28px] sm:text-[44px] leading-tight mb-8">{slide.title}</h2>
        <ul className="space-y-3 sm:space-y-4">
          {slide.items.map((item, i) => (
            <li key={i} className="rvp-slide-in flex items-start gap-3 sm:gap-4 rounded-xl border border-emerald-500/20 bg-emerald-950/15 px-4 sm:px-5 py-3 sm:py-4" style={{ animationDelay: `${0.25 + i * 0.13}s` }}>
              <div className="shrink-0 w-7 h-7 rounded-full border border-emerald-500/50 bg-emerald-500/20 flex items-center justify-center text-emerald-300 font-mono-spec text-[12px] font-bold">{i + 1}</div>
              <span className="text-white/90 text-[14px] sm:text-[16.5px] leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
        {slide.signature && (
          <div className="mt-8 pt-6 border-t border-white/10 text-[11.5px] sm:text-[13px] text-white/50 font-mono-spec text-center uppercase tracking-[0.22em]">{slide.signature}</div>
        )}
      </div>
    </div>
  );
}

/* ────────────── Slide : CTA final ────────────── */
export function SlideCta({ slide }) {
  return (
    <div className="h-full flex items-center justify-center px-8 sm:px-16 text-center">
      <div className="max-w-3xl">
        <h1 className="rvp-zoom-in font-serif-editorial text-white text-[38px] sm:text-[64px] leading-tight">{V(slide.title)}</h1>
        {slide.description && <p className="rvp-slide-up mt-6 text-white/70 text-[15px] sm:text-[20px] leading-relaxed max-w-2xl mx-auto" style={{ animationDelay: '0.35s' }}>{V(slide.description)}</p>}
        {slide.next_action && (
          <div className="rvp-pop-in mt-10 inline-flex items-center gap-2 px-5 py-3 rounded-full border border-red-500/40 bg-red-950/30 text-red-200 text-[13px] sm:text-[15px] font-semibold font-mono-spec uppercase tracking-wider" style={{ animationDelay: '0.65s' }}>
            <Sparkles size={14} /> {V(slide.next_action)}
          </div>
        )}
      </div>
    </div>
  );
}

/* ────────────── Slide : Tour de table (Phase 2) ────────────── */
export function SlideTourDeTable({ slide, joinUrl }) {
  return (
    <div className="h-full flex flex-col justify-center px-8 sm:px-16 py-10">
      <div className="max-w-4xl mx-auto w-full">
        <div className="text-[10.5px] sm:text-[12px] uppercase tracking-[0.28em] text-sky-400 font-mono-spec font-bold mb-3 inline-flex items-center gap-2">
          <MessageCircle size={13} /> Phase 2 · Découverte
        </div>
        <h2 className="rvp-slide-up font-serif-editorial text-white text-[28px] sm:text-[44px] leading-tight mb-6">{V(slide.title)}</h2>
        {slide.intro && <p className="rvp-slide-up text-white/70 text-[14.5px] sm:text-[17px] mb-6 leading-relaxed" style={{ animationDelay: '0.15s' }}>{V(slide.intro)}</p>}
        <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
          {slide.questions.map((q, i) => (
            <div key={i} className="rvp-pop-in rounded-xl border border-sky-500/25 bg-sky-950/15 p-4 sm:p-5" style={{ animationDelay: `${0.3 + i * 0.12}s` }}>
              <div className="text-sky-300 font-mono-spec text-[11px] font-bold uppercase tracking-wider mb-2">Question {i + 1}</div>
              <div className="text-white text-[15px] sm:text-[17px] font-serif-editorial leading-snug">{VF(q)}</div>
            </div>
          ))}
        </div>
        {slide.note && <div className="mt-6 text-[12.5px] text-white/50 italic border-l-2 border-white/15 pl-3">{V(slide.note)}</div>}
        {joinUrl && (
          <div className="mt-6 flex items-center gap-4 sm:gap-5 rounded-xl border border-emerald-500/30 bg-emerald-950/15 p-4" data-testid="slide-join-qr">
            <div className="bg-white p-2 sm:p-2.5 rounded-lg shrink-0"><QRCodeSVG value={joinUrl} size={104} level="M" /></div>
            <div>
              <div className="text-emerald-300 font-mono-spec text-[10.5px] sm:text-[12px] font-bold uppercase tracking-wider">Facultatif · Rejoignez avec votre téléphone</div>
              <p className="text-white/75 text-[12.5px] sm:text-[15px] mt-1.5 leading-relaxed">
                Scannez pendant le tour de table : indiquez votre prénom (si vous le souhaitez), suivez les diapositives,
                répondez aux questions — et retrouvez le rappel du cours à la fin.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ────────────── Slide : Objectif de séance (Phase 5) ────────────── */
export function SlideObjectif({ slide }) {
  return (
    <div className="h-full flex items-center justify-center px-8 sm:px-16">
      <div className="max-w-4xl text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/50 bg-amber-950/30 text-amber-300 text-[10.5px] sm:text-[12px] uppercase tracking-[0.22em] font-mono-spec font-bold mb-8">
          <Target size={13} /> Phase 5 · Objectif de la séance
        </div>
        <div className="text-white/50 text-[13px] sm:text-[15px] uppercase tracking-wider font-mono-spec mb-3">À la fin de cette séance…</div>
        <h1 className="font-serif-editorial text-white text-[34px] sm:text-[54px] lg:text-[64px] leading-[1.05] tracking-tight">{V(slide.objectif)}</h1>
        {slide.criteres && slide.criteres.length > 0 && (
          <div className="mt-10 pt-8 border-t border-white/10">
            <div className="text-[11px] sm:text-[12.5px] uppercase tracking-[0.22em] text-white/50 font-mono-spec font-bold mb-4">Critères de réussite</div>
            <ul className="grid sm:grid-cols-2 gap-3 text-left">
              {slide.criteres.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-white/80 text-[13.5px] sm:text-[15px]">
                  <Check size={16} className="text-emerald-400 mt-0.5 shrink-0" />
                  <span>{VF(c)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

/* ────────────── Slide : Bilan de séance (Phase 7) ────────────── */
export function SlideBilan({ slide }) {
  return (
    <div className="h-full flex flex-col justify-center px-8 sm:px-16 py-10">
      <div className="max-w-4xl mx-auto w-full">
        <div className="text-[10.5px] sm:text-[12px] uppercase tracking-[0.28em] text-emerald-400 font-mono-spec font-bold mb-3 inline-flex items-center gap-2">
          <ClipboardCheck size={13} /> Phase 7 · Bilan
        </div>
        <h2 className="rvp-slide-up font-serif-editorial text-white text-[28px] sm:text-[44px] leading-tight mb-8">{V(slide.title)}</h2>
        {slide.retenir && slide.retenir.length > 0 && (
          <div className="mb-6">
            <div className="rvp-fade-down text-emerald-300 text-[12px] uppercase tracking-wider font-bold font-mono-spec mb-3" style={{ animationDelay: '0.15s' }}>Ce qu&apos;on retient</div>
            <ul className="space-y-2.5">
              {slide.retenir.map((r, i) => (
                <li key={i} className="rvp-slide-in flex items-start gap-3 text-white/85 text-[15px] sm:text-[17px]" style={{ animationDelay: `${0.25 + i * 0.12}s` }}>
                  <div className="shrink-0 w-6 h-6 rounded-full border border-emerald-500/50 bg-emerald-500/15 text-emerald-300 font-mono-spec text-[11px] flex items-center justify-center">{i + 1}</div>
                  <span className="leading-relaxed">{VF(r)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {slide.questions_ouvertes && slide.questions_ouvertes.length > 0 && (
          <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.03] p-4 sm:p-5">
            <div className="text-white/60 text-[11.5px] uppercase tracking-wider font-mono-spec font-bold mb-3 inline-flex items-center gap-2"><MessageCircle size={12} /> Questions à débattre</div>
            <ul className="space-y-2">
              {slide.questions_ouvertes.map((q, i) => (
                <li key={i} className="text-white/75 text-[14px] sm:text-[15.5px] italic">« {VF(q)} »</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

/* ────────────── Slide : Message Sécurité Routière (Phase 9) ────────────── */
export function SlideMessageSR({ slide }) {
  return (
    <div className="h-full flex items-center justify-center px-8 sm:px-16 relative overflow-hidden">
      {/* Background halo */}
      <div className="absolute inset-0 bg-gradient-to-b from-red-950/20 via-transparent to-transparent pointer-events-none" />
      <div className="max-w-4xl text-center relative">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-500/60 bg-red-950/40 text-red-200 text-[10.5px] sm:text-[12px] uppercase tracking-[0.22em] font-mono-spec font-bold mb-8">
          <Shield size={13} /> Message de la Sécurité Routière
        </div>
        {slide.kicker && (
          <div className="text-white/50 text-[13px] sm:text-[15px] uppercase tracking-wider font-mono-spec mb-4">{slide.kicker}</div>
        )}
        <blockquote className="rvp-zoom-in font-serif-editorial text-white text-[32px] sm:text-[52px] lg:text-[62px] leading-[1.05] tracking-tight" style={{ animationDelay: '0.2s' }}>
          {V(slide.message)}
        </blockquote>
        {slide.chiffre && (
          <div className="rvp-pop-in mt-10 inline-flex items-center gap-4 px-6 py-4 rounded-2xl border border-red-500/40 bg-red-950/25" style={{ animationDelay: '0.9s' }}>
            <div className="rvp-impact font-serif-editorial text-red-400 text-[42px] sm:text-[64px] leading-none" style={{ animationDelay: '1s' }}>{slide.chiffre.number}</div>
            <div className="text-white/70 text-[13px] sm:text-[15px] text-left max-w-xs">{slide.chiffre.label}</div>
          </div>
        )}
        {slide.source && <div className="mt-6 text-[11px] sm:text-[12px] uppercase tracking-[0.22em] text-white/40 font-mono-spec">Source : {slide.source}</div>}
      </div>
    </div>
  );
}

/* ────────────── Registry ────────────── */
/* ────────────── Slide : QR Code (quiz ou URL custom) ────────────── */
export function SlideQrQuiz({ slide }) {
  const defaultUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/quiz/rvp/${slide.slug}`
    : `https://codevoiture.fr/quiz/rvp/${slide.slug}`;
  const url = slide.url || defaultUrl;
  return (
    <div className="h-full flex flex-col items-center justify-center px-8 sm:px-16">
      <div className="text-[11.5px] sm:text-[13px] uppercase tracking-[0.3em] text-red-400 font-mono-spec font-bold mb-4">
        {slide.kicker || 'Prolonger la séance à la maison'}
      </div>
      <div className="rvp-zoom-in font-serif-editorial text-white text-[38px] sm:text-[54px] leading-[1.05] text-center max-w-4xl">
        {slide.title || 'Refaire le quiz en famille'}
      </div>
      {slide.subtitle && (
        <p className="mt-5 text-white/70 text-[15px] sm:text-[17px] text-center max-w-2xl leading-relaxed">
          {slide.subtitle}
        </p>
      )}
      <div className="rvp-pop-in mt-10 flex flex-col items-center gap-4" style={{ animationDelay: '0.35s' }}>
        <div className="p-5 bg-white rounded-2xl shadow-xl">
          <QRCodeSVG value={url} size={260} level="H" />
        </div>
        <div className="text-white/60 font-mono-spec text-[12.5px] uppercase tracking-wider">
          {url.replace(/^https?:\/\//, '')}
        </div>
      </div>
      {slide.footnote && (
        <div className="mt-6 text-white/50 text-[12.5px] italic text-center max-w-2xl">
          {slide.footnote}
        </div>
      )}
    </div>
  );
}

export const SLIDE_REGISTRY = {
  title: SlideTitle,
  section: SlideSection,
  content: SlideContent,
  stat: SlideStat,
  qcm: SlideQcm,
  video: SlideVideo,
  image: SlideImage,
  quote: SlideQuote,
  engagement: SlideEngagement,
  cta: SlideCta,
  'tour-de-table': SlideTourDeTable,
  objectif: SlideObjectif,
  bilan: SlideBilan,
  'message-sr': SlideMessageSR,
  'bienvenue-perso': SlideBienvenue,
  'merci-perso': SlideMerci,
  'qr-quiz': SlideQrQuiz,
};
